import React, { useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { ContentSideFilterContainer } from 'containers/ContentSideFilterContainer';
import { Container } from 'semantic-ui-react';
import { useGoogleAds } from 'hooks/useGoogleAds';
import { PageHelmet } from 'components/atoms/PageHelmet/PageHelmet';
import { SearchNotFound } from 'components/pages/PageSearch/SearchNotFound';
import { HeaderPageWrapper } from 'components/templates/HeaderPageWrapper';
import { CombinedFiltersPaginationViewContainer } from 'containers/CombinedFiltersPaginationViewContainer';
import { Footer } from 'components/molecules/Footer';
import { Divider, OnlyMobileCSS } from 'components/atoms';
import { ExternalAdBanner, ExternalAdBannerType } from 'components/organisms/ExternalAdBanner/ExternalAdBanner';
import { sortByOptions, PaginationInt } from 'constants/index';
import {
  extractProductPayload,
  SEARCH_CORRECTION,
  SEARCH_FACET_UPDATE,
  SEARCH_PRODUCT_VIEW,
  handleEvent,
} from 'utils/Analytics';
import { Contentful } from 'mxp-schemas';

export interface Props {
  isAuth: boolean | null;
  results: State.FeedCardContentItem[];
  pagination: PaginationInt;
  searchQuery: string;
  suggested: string | null;
  sortBy: string;
  page: number;
  perPage: number;
  filtersStr: string; // subscription required to fire fetchSearchContent if changed
  resultsLoading: boolean;
  resultsFetched: boolean;
  fetchSearchContent(): void;
  popularSearches?: State.InternalLink[] | null;
  analyticsFacets: string[];
  skipSearchFrontloadRequest: boolean;
  getPopularSearches(): void;
  resetSearchModule: () => void;
  resetSkipSearchFrontloadRequest: () => void;
  externalAdsConfig: Contentful.ExternalAdsConfig;
}

export const PageSearch: React.FC<Props> = ({
  isAuth,
  searchQuery,
  sortBy,
  page,
  perPage,
  filtersStr,
  results,
  pagination,
  resultsLoading,
  resultsFetched,
  suggested,
  analyticsFacets,
  popularSearches,
  getPopularSearches,
  resetSearchModule,
  externalAdsConfig,
}) => {
  useGoogleAds(externalAdsConfig);
  const showNotFound: boolean = resultsFetched && !resultsLoading && !filtersStr && (!results || !results.length);

  const containerContentRef: any = React.useRef();

  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const toggleModalOpen = useCallback(() => setModalOpen(!modalOpen), [modalOpen, setModalOpen]);

  React.useEffect(() => resetSearchModule, [resetSearchModule]);

  useEffect(() => {
    const payload = extractProductPayload(results, pagination);
    const googletag = window.googletag || {};
    if (googletag?.pubadsReady) {
      googletag.pubads().refresh();
    }
    if (!payload) {
      return;
    }
    handleEvent(payload, SEARCH_PRODUCT_VIEW);
  }, [results, pagination]);

  useEffect(() => {
    handleEvent({ facets: analyticsFacets }, SEARCH_FACET_UPDATE);
  }, [(analyticsFacets || []).join(''), searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (suggested) {
      handleEvent({ value: searchQuery }, SEARCH_CORRECTION);
    }
  }, [suggested, searchQuery]);

  useEffect(() => {
    if (isAuth) getPopularSearches();
  }, [isAuth, getPopularSearches]);

  // Renders
  const renderSearchTextPostfix = (text: string) => {
    if (!text) return 'results';
    return (
      <>
        results for <HighlightedText>“{text.replace(/\+/g, ' ')}”</HighlightedText>
      </>
    );
  };

  const renderInformationLabel = () => {
    if (suggested) {
      return (
        <>
          Sorry we couldn't find any search {renderSearchTextPostfix(searchQuery)}. Here are search&nbsp;
          {renderSearchTextPostfix(suggested)}.
        </>
      );
    }
    return (
      <>
        Showing {pagination.from}–{pagination.to} of {pagination.total} search {renderSearchTextPostfix(searchQuery)}
      </>
    );
  };

  const renderBreadcrumb = (): React.ReactNode => {
    return (
      <Breadcrumb>
        <Container>
          <BreadcrumbWrapper>
            <BreadcrumbHome>Home</BreadcrumbHome>
            <BreadcrumbDivider>&gt;</BreadcrumbDivider>Search&nbsp;
            {renderSearchTextPostfix(suggested || searchQuery)}
          </BreadcrumbWrapper>
        </Container>
      </Breadcrumb>
    );
  };

  return (
    <div>
      <PageHelmet title={'Search'} />
      <OnlyMobileCSS>
        <ContentSideFilterContainer
          isModalOpen={modalOpen}
          closeModal={toggleModalOpen}
          resultsNumber={pagination.total}
          resultsFetched={resultsFetched}
        />
      </OnlyMobileCSS>
      <HeaderPageWrapper contentInnerRef={containerContentRef}>
        {showNotFound ? (
          <>
            {externalAdsConfig.topAdBanner && <ExternalAdBanner type={ExternalAdBannerType.TOP} />}
            <SearchNotFound searchQuery={searchQuery} popularSearches={popularSearches} />
            {externalAdsConfig.bottomAdBanner && <ExternalAdBanner type={ExternalAdBannerType.BOTTOM} />}
            <Footer />
          </>
        ) : (
          <>
            {renderBreadcrumb()}
            {externalAdsConfig.topAdBanner && <ExternalAdBanner type={ExternalAdBannerType.TOP} />}
            <StyledContainer>
              <InformationLabel>{renderInformationLabel()}</InformationLabel>
            </StyledContainer>
            <Divider />
            <CombinedFiltersPaginationViewContainer
              isPageSearch
              sortByOptionItems={Object.values(sortByOptions)}
              sortBy={sortBy}
              perPage={perPage}
              pagination={pagination}
              page={page}
              suggested={suggested}
              searchQuery={searchQuery}
              resultsFetched={resultsFetched}
              resultsLoading={resultsLoading}
              results={results}
              toggleModalOpen={toggleModalOpen}
              hasBottomAdBanner={Boolean(externalAdsConfig.bottomAdBanner)}
            />
            <Footer />
          </>
        )}
      </HeaderPageWrapper>
    </div>
  );
};

const StyledContainer = styled(Container)`
  margin-top: ${props => props.theme.pxToRem(20)};
`;

const InformationLabel = styled.span`
  font-size: ${props => props.theme.fontSizes.m};
  font-weight: ${props => props.theme.fontWeights.light};
  overflow-wrap: break-word;
  word-wrap: break-word;
`;

const Breadcrumb = styled.div`
  background-color: ${props => props.theme.colors.neutralGrey1};
  color: ${props => props.theme.colors.neutralGrey5};
`;

const BreadcrumbWrapper = styled.div`
  height: ${props => props.theme.pxToRem(48)};
  overflow: hidden;
  font-size: ${props => props.theme.fontSizes.xxs};
  line-height: ${props => props.theme.pxToRem(48)};
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const BreadcrumbHome = styled.span`
  color: ${props => props.theme.colors.primaryPurple};
`;

const BreadcrumbDivider = styled.span`
  display: inline-block;
  margin: 0 ${props => props.theme.pxToRem(8)};
`;

const HighlightedText = styled.span`
  font-weight: bold;
`;
